import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

function ScribeSessionList({ practiceId, onSelectSession }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    noteStatus: '',
    dateFrom: '',
    dateTo: ''
  });

  const loadSessions = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.noteStatus) params.noteStatus = filters.noteStatus;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      params.limit = 50;

      const res = await api.getScribeSessions(practiceId, params);
      setSessions(res.data || []);
    } catch (err) {
      console.error('Error loading scribe sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, filters]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleDelete = async (e, session) => {
    e.stopPropagation();
    if (!window.confirm('Delete this session? This cannot be undone.')) return;
    try {
      await api.deleteScribeSession(practiceId, session.guid);
      loadSessions();
    } catch (err) {
      alert('Failed to delete session: ' + err.message);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div style={styles.container}>
      {/* Filters */}
      <div style={styles.filterBar}>
        <select
          style={styles.filterSelect}
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
        >
          <option value="">All Statuses</option>
          <option value="streaming">Streaming</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          style={styles.filterSelect}
          value={filters.noteStatus}
          onChange={e => setFilters(f => ({ ...f, noteStatus: e.target.value }))}
        >
          <option value="">All Note Statuses</option>
          <option value="pending">Pending</option>
          <option value="generated">Generated</option>
          <option value="reviewed">Reviewed</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <input
          type="date"
          style={styles.filterInput}
          value={filters.dateFrom}
          onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
          placeholder="From"
        />
        <input
          type="date"
          style={styles.filterInput}
          value={filters.dateTo}
          onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
          placeholder="To"
        />
        <button style={styles.refreshBtn} onClick={loadSessions}>
          Refresh
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={styles.loading}>Loading sessions...</div>
      ) : sessions.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyTitle}>No scribe sessions found</p>
          <p style={styles.emptyText}>Start a new session to begin documenting encounters.</p>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Patient</th>
                <th style={styles.th}>Specialty</th>
                <th style={styles.th}>Duration</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Notes</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(session => (
                <tr
                  key={session.guid}
                  style={styles.tr}
                  onClick={() => onSelectSession(session)}
                >
                  <td style={styles.td}>{formatDate(session.created_at)}</td>
                  <td style={styles.td}>
                    <span style={styles.patientName}>
                      {session.patient_first_name} {session.patient_last_name}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.specialty}>
                      {(session.specialty || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDuration(session.duration_seconds)}</td>
                  <td style={styles.td}>
                    <SessionStatusBadge status={session.status} />
                    {session.error_message && (
                      <div style={styles.errorText} title={session.error_message}>{session.error_message}</div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <NoteStatusBadge status={session.note_status} />
                  </td>
                  <td style={styles.td}>
                    {['failed', 'cancelled'].includes(session.status) && (
                      <button
                        style={styles.deleteBtn}
                        onClick={(e) => handleDelete(e, session)}
                        title="Delete session"
                      >
                        Delete
                      </button>
                    )}
                    {session.note_status === 'generated' && (
                      <span style={styles.reviewHint}>Review</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SessionStatusBadge({ status }) {
  const map = {
    initializing: { bg: '#f3f4f6', color: '#6b7280' },
    streaming: { bg: '#dbeafe', color: '#1e40af' },
    paused: { bg: '#fef3c7', color: '#92400e' },
    processing: { bg: '#e0e7ff', color: '#3730a3' },
    completed: { bg: '#d1fae5', color: '#065f46' },
    failed: { bg: '#fee2e2', color: '#991b1b' },
    cancelled: { bg: '#f3f4f6', color: '#6b7280' }
  };
  const c = map[status] || map.initializing;
  return (
    <span style={{ ...styles.badge, backgroundColor: c.bg, color: c.color }}>
      {status}
    </span>
  );
}

function NoteStatusBadge({ status }) {
  const map = {
    pending: { bg: '#f3f4f6', color: '#6b7280' },
    generated: { bg: '#dbeafe', color: '#1e40af' },
    reviewed: { bg: '#e0e7ff', color: '#3730a3' },
    approved: { bg: '#d1fae5', color: '#065f46' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
    pushed_to_ehr: { bg: '#d1fae5', color: '#065f46' }
  };
  const c = map[status] || map.pending;
  return (
    <span style={{ ...styles.badge, backgroundColor: c.bg, color: c.color }}>
      {(status || 'pending').replace(/_/g, ' ')}
    </span>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  filterBar: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  filterSelect: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    backgroundColor: '#fff',
    color: '#374151'
  },
  filterInput: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    backgroundColor: '#fff',
    color: '#374151'
  },
  refreshBtn: {
    padding: '6px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: 500
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px'
  },
  empty: {
    padding: '60px 20px',
    textAlign: 'center'
  },
  emptyTitle: {
    margin: '0 0 4px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#6b7280'
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: '#9ca3af'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    backgroundColor: '#f9fafb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap'
  },
  tr: {
    cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6'
  },
  td: {
    padding: '10px 14px',
    color: '#374151',
    whiteSpace: 'nowrap'
  },
  patientName: {
    fontWeight: 500,
    color: '#1f2937'
  },
  specialty: {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'capitalize'
  },
  errorText: { marginTop: 4, fontSize: 11, color: '#991b1b', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  badge: {
    padding: '3px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'capitalize'
  },
  deleteBtn: {
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid #fca5a5',
    backgroundColor: '#fff',
    color: '#dc2626',
    fontSize: '12px',
    cursor: 'pointer'
  },
  reviewHint: {
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer'
  }
};

export default ScribeSessionList;
